/**
 * Shared quote-composer types used by both the standalone create page
 * (catalog-search) and the cart-fed Save Quote dialog (SPU-493/494).
 */

/** A single quote line being composed, independent of where it was sourced (catalog search or cart). */
export interface DraftLine {
  productId: number;
  productClass: string;
  productName: string;
  manufacturerPartNumber?: string;
  shiPartNumber?: string;
  imageUrl?: string;
  inventoryStatus?: string;
  unitPrice?: number;
  /** Cart line this was seeded from; lets the server price configured bundles and saved-quote lines from the cart. */
  sourceCartLineNumber?: number;
  /** The saved-quote item ({quoteGuid}_{quoteItemId}) the user reviewed on that cart line; the server rejects a stale cart line. */
  sourceQuoteItemDescriptor?: string;
}

/**
 * A composed row in the quote. Carries a stable client-side {@link id} so duplicate products (e.g. the
 * same item seeded twice from the cart) stay independent, editable rows instead of collapsing into one
 * shared quantity. Backend create preserves one downstream line per item, so duplicates persist through.
 */
export interface ComposedLine {
  id: number;
  product: DraftLine;
  quantity: number;
}

/** Ship-to address draft captured on the Shipping step for freight/tax preview and persistence. */
export interface ShippingAddressDraft {
  street: string;
  city: string;
  stateProvince: string;
  postalCode: string;
  country: string;
}

/** Empty ship-to draft (US default), reused when clearing/resetting the address selection. */
export const EMPTY_SHIPPING_ADDRESS: ShippingAddressDraft = {
  street: "",
  city: "",
  stateProvince: "",
  postalCode: "",
  country: "US",
};

/** Customer description cap; mirrors the backend CreateQuoteRequestDto [MaxLength(300)] boundary. */
export const DESCRIPTION_MAX_LENGTH = 300;

/** The three composer steps, in order. */
export const STEPS = ["Products", "Shipping & Tax", "Finish"] as const;
